import { RootState } from 'modules/Editor/store';
import { selectInfographHeightPx, selectInfographWidthPx } from 'modules/Editor/store/infographSelector';
import { store } from 'modules/Editor/store/store';
import { RefObject } from 'react';

import { DEFAULT_CENTER_RATIO } from './ZoomControl.config';

type PageScrollContainerRef = RefObject<HTMLDivElement> | null;
type PageFocusWrapperRef = RefObject<HTMLDivElement> | null;
type PageSizeAreaRef = RefObject<HTMLDivElement> | null;
type ScrollRatios = [number, number];

/**
 * Calculates the current scroll ratios for a page.
 *
 * calculate the current view center (center of pageScrollContainer) dimensions to the left and top relative to pageFocusWrapperW and pageFocusWrapperH
 *  so that we can keep the same ratios when changing the zoom level
 *
 * @param {PageScrollContainerRef} pageScrollContainerRef - A React ref object pointing to the scroll container element.
 * @param {PageFocusWrapperRef} pageFocusWrapperRef - A React ref object pointing to the wrapper element.
 *
 * @returns {ScrollRatios} An array containing two numbers: the X scroll ratio and the Y scroll ratio.
 * Each ratio is a value between 0 and 1, where 0 represents the start of the scrollable area and 1 represents the end.
 * If the pageFocusWrapperEl is larger than pageScrollEl, ratios are calculated, else, it returns 0.5 (center of the page).
 */
export const getCurrentScrollRatios = (
  pageScrollContainerRef: PageScrollContainerRef,
  pageFocusWrapperRef: PageFocusWrapperRef,
): ScrollRatios => {
  const pageScrollEl = pageScrollContainerRef?.current;
  const pageFocusWrapperEl = pageFocusWrapperRef?.current;
  let xRatio = DEFAULT_CENTER_RATIO;
  let yRatio = DEFAULT_CENTER_RATIO;
  if (pageScrollEl && pageFocusWrapperEl) {
    const { height: pageFocusWrapperH, width: pageFocusWrapperW } = pageFocusWrapperEl.getBoundingClientRect();
    const { height: pageScrollH, width: pageScrollW } = pageScrollEl.getBoundingClientRect();
    // if the dimension of pageFocusWrapperEl is larger than pageScrollEl, calculate the scroll ratio, else, page is centered at 0.5 ratio
    xRatio =
      pageFocusWrapperW > pageScrollW
        ? (pageScrollEl.scrollLeft + 0.5 * pageScrollW) / pageFocusWrapperW
        : DEFAULT_CENTER_RATIO;
    yRatio =
      pageFocusWrapperH > pageScrollH
        ? (pageScrollEl.scrollTop + 0.5 * pageScrollH) / pageFocusWrapperH
        : DEFAULT_CENTER_RATIO;
  }
  return [xRatio, yRatio];
};

/**
 * Scrolls the pageScrollContainer to make sure its view center is kept the same while zooming via zoom input and +/- buttons
 *
 *
 * @param {PageScrollContainerRef} pageScrollContainerRef
 * @param {PageFocusWrapperRef} pageFocusWrapperRef
 * @param {Array<number>} scrollRatios - An array of two numbers specifying the relative X and Y positions in the focus wrapper to which to scroll. Each value should be a ratio between 0 (start of the scrollable area) and 1 (end of the scrollable area).
 *
 * @returns {void} This function does not return anything; it modifies the scroll position of the scroll container element.
 */
export const scrollToFocusPosition = (
  pageScrollContainerRef: PageScrollContainerRef,
  pageFocusWrapperRef: PageFocusWrapperRef,
  scrollRatios: [number, number],
) => {
  const pageScrollEl = pageScrollContainerRef?.current;
  const pageFocusWrapperEl = pageFocusWrapperRef?.current;

  if (pageScrollEl && pageFocusWrapperEl) {
    const { height: pageFocusWrapperH, width: pageFocusWrapperW } = pageFocusWrapperEl.getBoundingClientRect();
    const { height: pageScrollH, width: pageScrollW } = pageScrollEl.getBoundingClientRect();

    pageScrollEl.scrollLeft = scrollRatios[0] * pageFocusWrapperW - DEFAULT_CENTER_RATIO * pageScrollW;
    pageScrollEl.scrollTop = scrollRatios[1] * pageFocusWrapperH - DEFAULT_CENTER_RATIO * pageScrollH;
  }
};

/**
 * This function is to remove height and width property set by Zoom Control logic when zoom_level update is complete,
 * Otherwise `width={scaledWidth} height={scaledHeight}` styling for <Page /> Component would get overriden by css styles set by adjustPageZoom function
 *
 * @param {PageSizeAreaRef} pageSizeAreaRef
 *
 * @returns {void} This function does not return anything; it modifies the CSS properties of the `pageSizeAreaRef` element.
 */
export const unsetPageSizeProperty = (pageSizeAreaRef: PageSizeAreaRef) => {
  const pageSizeEl = pageSizeAreaRef?.current;
  if (pageSizeEl) {
    pageSizeEl.style.removeProperty('height');
    pageSizeEl.style.removeProperty('width');
  }
};

/**
 * Adjusts the zoom level of the editor page by directly mutating the sizeEl and scaleEl elements
 *
 * @param {number} tempZoom - The new zoom level. This is a multiplicative factor, where 1 represents the default size.
 * @param {HTMLDivElement | null | undefined} sizeEl
 * @param {HTMLDivElement | null | undefined} scaleEl
 *
 * @returns {void} The function does not return anything.
 *
 */
export const adjustPageZoom = (
  tempZoom: number,
  sizeEl: HTMLDivElement | null | undefined,
  scaleEl: HTMLDivElement | null | undefined,
) => {
  if (sizeEl && scaleEl) {
    const state = store.getState() as RootState;
    const heightPx = selectInfographHeightPx(state);
    const widthPx = selectInfographWidthPx(state);

    sizeEl.style.width = `${widthPx * tempZoom}px`;
    sizeEl.style.height = `${heightPx * tempZoom}px`;
    scaleEl.style.transform = `scale(${tempZoom})`;
  }
};
